// FIX-CCS-A2-PLUMBING (unit A2, R16) — the offline-photo kill switch, read on the phone.
//
// Reads get_offline_photo_policy(p_session_token) (SECURITY DEFINER; the farmer learns only its
// own verdict) at app open (mounted in App.tsx) and at camera open (CameraScreen invalidates it
// when online). The last verdict is cached (TanStack persisted cache + localStorage). If it has
// never been read, the switch is OFF. It changes NO behaviour yet: the Stage B outbox will branch
// on `enabled`; today nothing consumes it except for logging.
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';
import { recordServerNow } from '@/shared/lib/serverClock';

const STORAGE_KEY = 'agxl_offline_photo_policy';
export const OFFLINE_PHOTO_POLICY_QUERY_KEY = 'offline-photo-policy';
/** SD-O49: a verdict older than this is re-read (was 24 h; a switch flip took a day to reach a phone). */
export const POLICY_STALE_MS = 60 * 60 * 1000;

/** True when the verdict has never been read or its read is older than POLICY_STALE_MS. */
export function isPolicyStale(policy: OfflinePhotoPolicy, nowMs: number = Date.now()): boolean {
  const t = policy.checked_at ? Date.parse(policy.checked_at) : NaN;
  return !Number.isFinite(t) || nowMs - t > POLICY_STALE_MS;
}

/** SD-O49 → INV-FIX-CCS-D-SAVE-ERROR (CameraScreen Save): no signal and the outbox not enabled → tell the farmer. A live
 *  send with no signal can only fail, whatever the verdict's age: since the verdict is re-read on resume/online, a FRESH
 *  OFF is the normal state of a switched-off phone, and the old "stale or absent" clause sent exactly that phone down
 *  the live path ("Save failed: Failed to fetch", Live 2026-10-06 15:2xZ). Online is never blocked; with the outbox
 *  enabled the queue path runs instead. `policy` is accepted for the call site and no longer consulted. */
export function offlineSaveBlocked(a: { online: boolean; outboxEnabled: boolean; policy?: OfflinePhotoPolicy; nowMs?: number }): boolean {
  return !a.online && !a.outboxEnabled;
}

export interface OfflinePhotoPolicy {
  enabled: boolean;
  reason: string;
  checked_at: string | null;
}

const OFF_NEVER_READ: OfflinePhotoPolicy = { enabled: false, reason: 'never_read', checked_at: null };

/** The last cached verdict, or OFF if it has never been read (safe default). */
export function getCachedOfflinePhotoPolicy(): OfflinePhotoPolicy {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return OFF_NEVER_READ;
    const parsed = JSON.parse(raw) as OfflinePhotoPolicy;
    return { enabled: parsed?.enabled === true, reason: String(parsed?.reason ?? 'unknown'), checked_at: parsed?.checked_at ?? null };
  } catch {
    return OFF_NEVER_READ;
  }
}

async function fetchPolicy(sessionToken: string): Promise<OfflinePhotoPolicy> {
  const { data, error } = await supabase.rpc('get_offline_photo_policy', { p_session_token: sessionToken });
  if (error) throw error;
  const body = (data ?? {}) as { enabled?: boolean; reason?: string; server_now?: string };
  recordServerNow(body.server_now, 'get_offline_photo_policy');
  const policy: OfflinePhotoPolicy = {
    enabled: body.enabled === true,
    reason: String(body.reason ?? 'unknown'),
    checked_at: body.server_now ?? new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(policy));
  } catch {
    /* storage blocked — the query cache still holds the verdict */
  }
  return policy;
}

/** The farmer's offline-photo verdict. OFF until a read succeeds; the last read survives offline restarts. */
export function useOfflinePhotoPolicy(): OfflinePhotoPolicy {
  const { user } = useAuth();
  const token = user?.sessionToken ?? null;
  const { data } = useQuery({
    queryKey: [OFFLINE_PHOTO_POLICY_QUERY_KEY, user?.id ?? 'anon'],
    queryFn: () => fetchPolicy(token as string),
    enabled: !!token,
    // SD-O49: 1 h, and re-read on 'online' and on app resume (OutboxProvider) besides camera open.
    staleTime: POLICY_STALE_MS,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    retry: 1,
  });
  const policy = data ?? getCachedOfflinePhotoPolicy();
  useEffect(() => {
    if (data) console.info('[offline-photo-policy]', data.enabled ? 'ON' : 'OFF', data.reason);
  }, [data]);
  return policy;
}
